import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { toast } from 'react-toastify';
import Loader from '../../../components/Loader';
import Paginator from '../../../components/Paginator';
import PortalApiService from '../../../services/PortalApiService';

const currency = process.env.REACT_APP_CURRENCY || 'AED';

const PortalServiceList = () => {
    const [services, setServices] = useState([]);
    const [counts, setCounts] = useState({ all: 0, active: 0, disabled: 0 });
    const [pagination, setPagination] = useState({});
    const [isLoader, setIsLoader] = useState(false);
    const [apiData, setApiData] = useState({ search: '', status: '', page: 1 });

    useEffect(() => {
        getServices();
    }, [apiData.page, apiData.search, apiData.status]);

    const getServices = async () => {
        try {
            setIsLoader(true);
            const { data } = await PortalApiService.request({ method: 'GET', url: '/services', data: { ...apiData, limit: 15 } });
            if (data.status) {
                setServices(data.data);
                setCounts(data.counts);
                setPagination(data.pagination);
            }
        } catch (error) {
            // PortalApiService already shows the error toast
        } finally {
            setIsLoader(false);
        }
    };

    const toggleStatus = async (id) => {
        try {
            const { data } = await PortalApiService.request({ method: 'POST', url: `/services/status/${id}` });
            if (data.status) {
                toast.success(data.message);
                getServices();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // PortalApiService already shows the error toast
        }
    };

    const deleteService = async (id) => {
        const result = await Swal.fire({
            title: 'Delete Service?',
            text: 'Users will no longer see this service.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Yes, delete it!',
            cancelButtonColor: '#9E9B9B',
        });
        if (!result.isConfirmed) return;
        try {
            const { data } = await PortalApiService.request({ method: 'DELETE', url: `/services/${id}` });
            if (data.status) {
                toast.success(data.message);
                getServices();
            } else {
                toast.error(data.message);
            }
        } catch (error) {
            // PortalApiService already shows the error toast
        }
    };

    const priceLabel = (service) => {
        const prices = (service.durations || []).map(d => d.discount_price);
        return prices.length ? `from ${currency} ${Math.min(...prices)}` : '-';
    };

    const tabs = [
        { key: '', label: 'All', count: counts.all },
        { key: 'true', label: 'Active', count: counts.active },
        { key: 'false', label: 'Disabled', count: counts.disabled },
    ];

    return (
        <div className="d-flex flex-column flex-column-fluid">
            <div className="app-toolbar py-3 py-lg-6">
                <div className="app-container container-xxl d-flex flex-stack">
                    <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
                        <h1 className="page-heading text-dark fw-bold fs-3 my-0">My Services</h1>
                        <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                            <li className="breadcrumb-item text-muted">
                                <Link to="/dashboard" className="text-muted text-hover-primary">Home</Link>
                            </li>
                            <li className="breadcrumb-item">
                                <span className="bullet bg-gray-400 w-5px h-2px"></span>
                            </li>
                            <li className="breadcrumb-item text-muted">Services</li>
                        </ul>
                    </div>
                    <Link to="/services/create" className="btn btn-sm fw-bold btn-primary">
                        <i className="bi bi-plus-lg"></i> Add Service
                    </Link>
                </div>
            </div>

            <div className="app-content flex-column-fluid">
                <div className="app-container container-xxl">
                    <div className="card">
                        <div className="card-header border-0 pt-6 d-flex flex-wrap gap-3">
                            <ul className="nav nav-tabs nav-line-tabs fs-6 border-0">
                                {tabs.map(t => (
                                    <li className="nav-item" key={t.key}>
                                        <button
                                            className={`nav-link ${apiData.status === t.key ? 'active' : ''}`}
                                            onClick={() => setApiData({ ...apiData, status: t.key, page: 1 })}
                                        >
                                            {t.label} <span className="badge badge-light ms-1">{t.count}</span>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                            <input
                                type="text"
                                className="form-control form-control-solid w-250px"
                                placeholder="Search services..."
                                value={apiData.search}
                                onChange={e => setApiData({ ...apiData, search: e.target.value, page: 1 })}
                            />
                        </div>
                        <div className="card-body pt-0">
                            <div className="table-responsive">
                                <table className="table align-middle table-hover fs-6 gy-5">
                                    <thead>
                                        <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                                            <th>Service</th>
                                            <th>Category</th>
                                            <th>Price</th>
                                            <th>Active</th>
                                            <th className="text-end">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="fw-semibold text-gray-600 fs-6">
                                        {services.length > 0 ? services.map(service => (
                                            <tr key={service._id}>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        {service.images?.[0] && (
                                                            <div className="symbol symbol-50px me-3">
                                                                <img src={service.images[0]} alt={service.name} style={{ objectFit: 'cover' }} />
                                                            </div>
                                                        )}
                                                        <div>
                                                            <div className="fw-bold text-dark">{service.name}</div>
                                                            <div className="text-muted fs-7 text-truncate" style={{ maxWidth: '280px' }}>{service.short_desc}</div>
                                                        </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <div className="text-dark">{service.category_id?.title || '-'}</div>
                                                    <div className="text-muted fs-7">{service.sub_category_id?.title}</div>
                                                </td>
                                                <td className="fw-bold text-dark">{priceLabel(service)}</td>
                                                <td>
                                                    <div className="form-check form-switch form-check-custom form-check-solid">
                                                        <input className="form-check-input" type="checkbox" checked={!!service.status}
                                                            onChange={() => toggleStatus(service._id)} />
                                                    </div>
                                                </td>
                                                <td className="text-end">
                                                    <Link to={`/services/create/${service._id}`} className="btn btn-sm btn-light-info me-2" title="Edit">
                                                        <i className="bi bi-pencil-fill"></i>
                                                    </Link>
                                                    <button onClick={() => deleteService(service._id)} className="btn btn-sm btn-light-danger" title="Delete">
                                                        <i className="bi bi-trash"></i>
                                                    </button>
                                                </td>
                                            </tr>
                                        )) : (
                                            <tr>
                                                <td colSpan="5" className="text-center text-muted py-10">
                                                    {isLoader ? '' : (
                                                        <>
                                                            <div className="mb-3">No services yet</div>
                                                            <Link to="/services/create" className="btn btn-sm btn-light-primary">Add your first service</Link>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                            {isLoader && <Loader text="Loading" size="30" />}
                            {pagination?.total_pages > 1 && (
                                <Paginator
                                    page={pagination.current_page}
                                    rows={pagination.per_page}
                                    totalRecords={pagination.total_records}
                                    onPageChange={page => setApiData(prev => ({ ...prev, page }))}
                                />
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PortalServiceList;
